import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Switch,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { ExternalLink, Mail, UserPlus, Trash2 } from 'lucide-react';
import { API_URL } from '../globals';
import { supabase } from '../supabaseClient';
import axios from 'axios';

const OrganizationTab = ({
  user,
  handleManageSubscription,
  isLoadingSubscription,
  broadcastPRSummaries,
  handleBroadcastPRSummariesChange,
}) => {
  // Invitation states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('USER');
  const [isInviting, setIsInviting] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteError, setInviteError] = useState('');

  // Team member states
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  useEffect(() => {
    if (user?.team?.id) {
      fetchTeamInvitations();
      fetchTeamMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchTeamInvitations = async () => {
    if (!user?.team?.id) return;

    setIsLoadingInvitations(true);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await axios.get(`${API_URL}/team-invitations?team_id=${user.team.id}`, { headers });
      if (response.data) {
        setInvitations(response.data.invitations || []);
      }
    } catch (error) {
      console.error('Error fetching team invitations:', error);
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!user?.team?.id) return;

    setIsLoadingMembers(true);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await axios.get(`${API_URL}/get-team-members?team_id=${user.team.id}`, { headers });
      if (response.data) {
        setTeamMembers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleInviteTeamMember = async (e) => {
    e.preventDefault();

    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setIsInviting(true);
    setInviteError('');
    setInviteMessage('');

    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await axios.post(
        `${API_URL}/invite-team-member`,
        { email: inviteEmail.trim(), team_id: user?.team?.id, role: inviteRole },
        { headers }
      );
      if (response.status === 200) {
        setInviteMessage('Invitation sent successfully!');
        setInviteEmail('');
        setInviteRole('USER');
        fetchTeamInvitations();
      }
    } catch (error) {
      console.error('Error inviting team member:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send invitation';
      setInviteError(errorMessage);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    const confirmRemove = window.confirm(`Are you sure you want to remove ${memberName} from the team? This action cannot be undone.`);
    if (!confirmRemove) return;

    setRemovingMemberId(memberId);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await axios.post(
        `${API_URL}/remove-member-from-team`,
        { member_id: memberId, team_id: user?.team?.id },
        { headers }
      );
      if (response.status === 200) {
        fetchTeamMembers();
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      const errorMessage = error.response?.data?.message || 'Failed to remove team member';
      alert(errorMessage);
    } finally {
      setRemovingMemberId(null);
    }
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 6 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#fff', mb: 1, fontSize: '1.75rem' }}>
            Organization Settings
          </Typography>
          <Typography sx={{ color: '#888', fontSize: '0.875rem', maxWidth: '460px', lineHeight: 1.6 }}>
            Configure your organization preferences, team members, and billing settings.
          </Typography>
        </Box>
      </Box>

      {/* Main Card */}
      <Box sx={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', p: 4, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Org Info */}
          <Box>
            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>ORGANIZATION NAME</Typography>
            <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>{user?.team?.company_name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>ORGANIZATION TIER</Typography>
            <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>{user?.team?.tier || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>SEAT COUNT</Typography>
            <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>{user?.team?.seat_count || 'N/A'}</Typography>
          </Box>

          <Divider sx={{ borderColor: '#2a2a2a', my: 2 }} />

          {/* Subscription */}
          <Box>
            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>SUBSCRIPTION</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={user?.team?.tier || 'N/A'} size="small" sx={{ backgroundColor: '#252525', color: '#fff', fontSize: '0.75rem', height: '24px' }} />
              <Button onClick={handleManageSubscription} startIcon={isLoadingSubscription ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ExternalLink size={16} />} disabled={isLoadingSubscription} sx={{ border: '1px solid #333', color: '#fff', textTransform: 'none', fontSize: '0.875rem', px: 2, '&:hover': { backgroundColor: '#252525' }, '&.Mui-disabled': { color: '#666', borderColor: '#333' } }}>
                {isLoadingSubscription ? 'Loading...' : 'Manage Seats'}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#2a2a2a', my: 2 }} />

          {/* Team Members */}
          <Box>
            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>TEAM MEMBERS</Typography>

            {/* Member List */}
            <Box sx={{ mb: 4 }}>
              {isLoadingMembers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: '#888' }} /></Box>
              ) : teamMembers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3, color: '#666', fontSize: '0.875rem' }}>No team members found</Box>
              ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#252525', border: '1px solid #333', borderRadius: '6px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { color: '#888', fontSize: '0.75rem', fontWeight: 600, borderColor: '#333' } }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teamMembers.map((member) => (
                        <TableRow key={member.id} sx={{ '& td': { color: '#fff', fontSize: '0.875rem', borderColor: '#333' } }}>
                          <TableCell>{member.full_name || 'N/A'}</TableCell>
                          <TableCell>{member.email}</TableCell>
                          <TableCell><Chip label={capitalizeFirstLetter(member.role) || 'User'} size="small" sx={{ backgroundColor: member.role?.toUpperCase() === 'OWNER' ? '#4caf5033' : '#333', color: member.role?.toUpperCase() === 'OWNER' ? '#4caf50' : '#fff', fontSize: '0.75rem', height: '20px' }} /></TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleRemoveMember(member.id, member.full_name)}
                              disabled={removingMemberId === member.id}
                              size="small"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Invite Form */}
            <Box
              component="form"
              onSubmit={handleInviteTeamMember}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mb: 3,
                p: 3,
                backgroundColor: '#252525',
                borderRadius: '6px',
                border: '1px solid #333',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <UserPlus size={16} color="#888" />
                <Typography sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 500 }}>
                  Invite Team Member
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Email Address"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#1a1a1a',
                      color: '#fff',
                      '& fieldset': { borderColor: '#333' },
                      '&:hover fieldset': { borderColor: '#555' },
                      '&.Mui-focused fieldset': { borderColor: '#fff' },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#888',
                      '&.Mui-focused': { color: '#fff' },
                    },
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: '#888', '&.Mui-focused': { color: '#fff' } }}>Role</InputLabel>
                  <Select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    label="Role"
                    sx={{
                      backgroundColor: '#1a1a1a',
                      color: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                      '& .MuiSvgIcon-root': { color: '#888' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#2a2a2a',
                          '& .MuiMenuItem-root': {
                            color: '#fff',
                            '&:hover': { backgroundColor: '#333' },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="USER">User</MenuItem>
                    <MenuItem value="ADMIN">Admin</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isInviting || !inviteEmail.trim()}
                  startIcon={isInviting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Mail size={16} />}
                  sx={{
                    backgroundColor: '#fff',
                    color: '#121212',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    px: 3,
                    '&:hover': { backgroundColor: '#e0e0e0' },
                    '&.Mui-disabled': { backgroundColor: '#333', color: '#666' },
                  }}
                >
                  {isInviting ? 'Sending...' : 'Send Invite'}
                </Button>
              </Box>

              {inviteMessage && (
                <Alert severity="success" sx={{ backgroundColor: '#1b5e201a', color: '#4caf50', border: '1px solid #4caf5033', '& .MuiAlert-icon': { color: '#4caf50' } }}>
                  {inviteMessage}
                </Alert>
              )}

              {inviteError && (
                <Alert severity="error" sx={{ backgroundColor: '#d32f2f1a', color: '#f44336', border: '1px solid #f4433633', '& .MuiAlert-icon': { color: '#f44336' } }}>
                  {inviteError}
                </Alert>
              )}
            </Box>

            {/* Pending Invitations List */}
            <Box>
              <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 2, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                PENDING INVITATIONS
              </Typography>

              {isLoadingInvitations ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} sx={{ color: '#888' }} />
                </Box>
              ) : invitations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3, color: '#666', fontSize: '0.875rem' }}>No pending invitations</Box>
              ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#252525', border: '1px solid #333', borderRadius: '6px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { color: '#888', fontSize: '0.75rem', fontWeight: 600, borderColor: '#333' } }}>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Invited</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invitations.map((invitation) => (
                        <TableRow key={invitation.id} sx={{ '& td': { color: '#fff', fontSize: '0.875rem', borderColor: '#333' } }}>
                          <TableCell>{invitation.email}</TableCell>
                          <TableCell>
                            <Chip label={invitation.role} size="small" sx={{ backgroundColor: '#333', color: '#fff', fontSize: '0.75rem', height: '20px' }} />
                          </TableCell>
                          <TableCell>{invitation.created_at ? new Date(invitation.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            <Chip label="Pending" size="small" sx={{ backgroundColor: '#ff9800', color: '#000', fontSize: '0.75rem', height: '20px' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default OrganizationTab; 